import { jsPDF } from 'jspdf';
import { DISCLOSURE_SECTIONS } from '@/components/pickup/DisclosureText';

interface PdfInput {
  woNumber: string;
  customerName: string;
  company: string;
  siteName: string;
  scheduledDate: string;
  woType: string;
  operator: string;
  estimates: { category: string; value: number; unit: string }[];
  notes: string;
  signerName: string;
  signatureDataUrl: string;
  signedAt: string;
  timezone: string;
}

export async function generatePickupPdf(input: PdfInput): Promise<Blob> {
  const doc = new jsPDF({ unit: 'mm', format: 'letter' });
  const pageW = doc.internal.pageSize.getWidth();
  const marginL = 20;
  const marginR = 20;
  const contentW = pageW - marginL - marginR;
  let y = 20;

  function checkPage(needed: number) {
    if (y + needed > doc.internal.pageSize.getHeight() - 20) {
      doc.addPage();
      y = 20;
    }
  }

  // Title
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Pickup Summary', marginL, y);
  y += 10;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Work Order: ${input.woNumber}`, marginL, y);
  y += 5;
  doc.text(`Customer: ${input.customerName} — ${input.company}`, marginL, y);
  y += 5;
  doc.text(`Site: ${input.siteName}`, marginL, y);
  y += 5;
  doc.text(`Type: ${input.woType}    |    Scheduled: ${input.scheduledDate}    |    Operator: ${input.operator}`, marginL, y);
  y += 10;

  // Divider
  doc.setDrawColor(200);
  doc.line(marginL, y, pageW - marginR, y);
  y += 6;

  // Estimates
  if (input.estimates.length > 0) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Pre-Intake Estimates', marginL, y);
    y += 6;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Category', marginL, y);
    doc.text('Amount', marginL + 90, y, { align: 'right' });
    doc.text('Unit', marginL + 100, y);
    y += 1;
    doc.line(marginL, y, pageW - marginR, y);
    y += 4;

    doc.setFont('helvetica', 'normal');
    for (const est of input.estimates) {
      checkPage(6);
      doc.text(est.category, marginL, y);
      doc.text(String(est.value), marginL + 90, y, { align: 'right' });
      doc.text(est.unit, marginL + 100, y);
      y += 5;
    }
    y += 4;
  }

  // Notes
  checkPage(20);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Pickup Notes', marginL, y);
  y += 6;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const noteLines = doc.splitTextToSize(input.notes, contentW);
  for (const line of noteLines) {
    checkPage(5);
    doc.text(line, marginL, y);
    y += 4;
  }
  y += 6;

  // Disclosure
  checkPage(20);
  doc.setDrawColor(200);
  doc.line(marginL, y, pageW - marginR, y);
  y += 6;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Terms & Conditions', marginL, y);
  y += 6;

  for (const section of DISCLOSURE_SECTIONS) {
    checkPage(20);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(section.title, marginL, y);
    y += 5;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    const bodyLines = doc.splitTextToSize(section.body, contentW);
    for (const line of bodyLines) {
      checkPage(4);
      doc.text(line, marginL, y);
      y += 3.5;
    }
    if (section.bullets) {
      for (const bullet of section.bullets) {
        checkPage(8);
        const bLines = doc.splitTextToSize(`• ${bullet}`, contentW - 4);
        for (const bl of bLines) {
          doc.text(bl, marginL + 4, y);
          y += 3.5;
        }
      }
    }
    y += 4;
  }

  // Signature block
  checkPage(50);
  doc.setDrawColor(200);
  doc.line(marginL, y, pageW - marginR, y);
  y += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Signed By:', marginL, y);
  doc.setFont('helvetica', 'normal');
  doc.text(input.signerName, marginL + 25, y);
  y += 5;
  const formattedDate = new Date(input.signedAt).toLocaleString('en-US', {
    timeZone: input.timezone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    timeZoneName: 'short',
  });
  doc.text(`Date: ${formattedDate}`, marginL, y);
  y += 8;

  // Signature image
  try {
    doc.addImage(input.signatureDataUrl, 'PNG', marginL, y, 70, 25);
    y += 30;
  } catch {
    doc.text('[Signature captured digitally]', marginL, y);
    y += 6;
  }

  return doc.output('blob');
}
